import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Image,
  SafeAreaView,
  Platform,
  useWindowDimensions,
  RefreshControl,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import { useFocusEffect } from '@react-navigation/native';
import LoadingAnimation from '../components/LoadingAnimation';
import { getProfilePictureUrl } from '../services/storage';
import {
  getFriends,
  getPendingFriendRequests,
  getSentFriendRequests,
  respondToFriendRequest,
  cancelFriendRequest,
  removeFriend,
} from '../services/database';
import { supabase } from '../services/supabase';

const DESKTOP_BREAKPOINT = 1024;
const MAX_CONTENT_WIDTH = 680;

const FriendsScreen = ({ navigation, route }) => {
  const { appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const { width } = useWindowDimensions();
  
  const isWeb = Platform.OS === 'web';
  const isDesktop = isWeb && width >= DESKTOP_BREAKPOINT;

  const [activeTab, setActiveTab] = useState('friends'); // 'friends', 'requests', 'sent'
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    const initialTab = route?.params?.initialTab;
    if (initialTab === 'friends' || initialTab === 'requests' || initialTab === 'sent') {
      setActiveTab(initialTab);
    }
  }, [route?.params?.initialTab]);

  const fetchData = async () => {
    if (!appUser?.id) return;
    
    try {
      const [friendsData, pendingData, sentData] = await Promise.all([
        getFriends(appUser.id),
        getPendingFriendRequests(appUser.id),
        getSentFriendRequests(appUser.id),
      ]);
      
      setFriends(friendsData);
      setPendingRequests(pendingData);
      setSentRequests(sentData);
    } catch (error) {
      console.error('Error fetching friends data:', error);
      showToast('Failed to load friends', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [appUser?.id])
  );

  // Real-time subscription for friend requests
  useEffect(() => {
    if (!appUser?.id) return;

    const subscription = supabase
      .channel('friend_requests_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friend_requests',
          filter: `or(sender_id=eq.${appUser.id},receiver_id=eq.${appUser.id})`,
        },
        () => {
          fetchData();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [appUser?.id]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleAcceptRequest = async (request) => {
    setProcessingId(request.id);
    try {
      await respondToFriendRequest(request.id, 'ACCEPTED');
      showToast(`You are now friends with ${request.sender.username}!`, 'success');
      fetchData();
    } catch (error) {
      console.error('Error accepting friend request:', error);
      showToast('Failed to accept request', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeclineRequest = async (request) => {
    setProcessingId(request.id);
    try {
      await respondToFriendRequest(request.id, 'DECLINED');
      showToast('Friend request declined', 'info');
      fetchData();
    } catch (error) {
      console.error('Error declining friend request:', error);
      showToast('Failed to decline request', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelRequest = async (request) => {
    setProcessingId(request.id);
    try {
      await cancelFriendRequest(request.id);
      showToast('Friend request cancelled', 'info');
      fetchData();
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      showToast('Failed to cancel request', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveFriend = async (friend) => {
    const confirmRemove = Platform.OS === 'web'
      ? window.confirm(`Remove ${friend.username} from friends?`)
      : await new Promise((resolve) => {
          Alert.alert(
            'Remove Friend',
            `Remove ${friend.username} from friends?`,
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Remove', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmRemove) return;

    setProcessingId(friend.friendshipId);
    try {
      await removeFriend(appUser.id, friend.id);
      showToast(`Removed ${friend.username} from friends`, 'info');
      fetchData();
    } catch (error) {
      console.error('Error removing friend:', error);
      showToast('Failed to remove friend', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleUserPress = (user) => {
    navigation.navigate('UserDetail', { userId: user.id });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderFriendItem = ({ item: friend }) => (
    <TouchableOpacity
      style={[styles.userCard, { backgroundColor: colors.surface }]}
      onPress={() => handleUserPress(friend)}
    >
      <View style={styles.userInfo}>
        {friend.profile_picture_url ? (
          <Image source={{ uri: getProfilePictureUrl(friend.profile_picture_url, 'thumbnail') || friend.profile_picture_url }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatarPlaceholder, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f0f0f0' }]}>
            <Icon name="person" size={24} color={colors.primary} />
          </View>
        )}
        <View style={styles.userDetails}>
          <Text style={[styles.username, { color: colors.text }]}>{friend.username}</Text>
          <View style={styles.statusRow}>
            <View style={[styles.onlineDot, { backgroundColor: friend.is_online ? '#4CAF50' : '#9E9E9E' }]} />
            <Text style={[styles.statusText, { color: colors.textSecondary }]}>
              {friend.is_online ? 'Online' : 'Offline'}
            </Text>
            {friend.rating && (
              <Text style={[styles.ratingText, { color: colors.textSecondary }]}>
                • {friend.rating} rating
              </Text>
            )}
          </View>
        </View>
      </View>
      <View style={styles.userActions}>
        <TouchableOpacity
          style={[styles.messageBtn, { backgroundColor: colors.primary }]}
          onPress={(e) => {
            e.stopPropagation();
            // Navigate to Chat tab with conversation params
            navigation.navigate('Chat', { 
              openUserId: friend.id, 
              openUser: friend 
            });
          }}
        >
          <Icon name="chat" size={18} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.moreBtn}
          onPress={(e) => {
            e.stopPropagation();
            handleRemoveFriend(friend);
          }}
          disabled={processingId === friend.friendshipId}
        >
          <Icon name="more-vert" size={20} color={colors.textSecondary} />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderRequestItem = ({ item: request }) => {
    const user = request.sender;
    const isProcessing = processingId === request.id;

    return (
      <View style={[styles.requestCard, { backgroundColor: colors.surface }]}>
        <TouchableOpacity style={styles.userInfo} onPress={() => handleUserPress(user)}>
          {user.profile_picture_url ? (
            <Image source={{ uri: getProfilePictureUrl(user.profile_picture_url, 'thumbnail') || user.profile_picture_url }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f0f0f0' }]}>
              <Icon name="person" size={24} color={colors.primary} />
            </View>
          )}
          <View style={styles.userDetails}>
            <Text style={[styles.username, { color: colors.text }]}>{user.username}</Text>
            <Text style={[styles.timeText, { color: colors.textSecondary }]}>
              Sent {formatTimeAgo(request.created_at)}
            </Text>
          </View>
        </TouchableOpacity>
        <View style={styles.requestActions}>
          <TouchableOpacity
            style={[styles.acceptBtn, { backgroundColor: colors.primary }]}
            onPress={() => handleAcceptRequest(request)}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <LoadingAnimation size={20} />
            ) : (
              <>
                <Icon name="check" size={18} color="#fff" />
                <Text style={styles.acceptBtnText}>Accept</Text>
              </>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.declineBtn, { borderColor: colors.border }]}
            onPress={() => handleDeclineRequest(request)}
            disabled={isProcessing}
          >
            <Icon name="close" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderSentRequestItem = ({ item: request }) => {
    const user = request.receiver;
    const isProcessing = processingId === request.id;

    return (
      <View style={[styles.requestCard, { backgroundColor: colors.surface }]}>
        <TouchableOpacity style={styles.userInfo} onPress={() => handleUserPress(user)}>
          {user.profile_picture_url ? (
            <Image source={{ uri: getProfilePictureUrl(user.profile_picture_url, 'thumbnail') || user.profile_picture_url }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f0f0f0' }]}>
              <Icon name="person" size={24} color={colors.primary} />
            </View>
          )}
          <View style={styles.userDetails}>
            <Text style={[styles.username, { color: colors.text }]}>{user.username}</Text>
            <Text style={[styles.timeText, { color: colors.textSecondary }]}>
              Sent {formatTimeAgo(request.created_at)}
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.cancelBtn, { borderColor: colors.border }]}
          onPress={() => handleCancelRequest(request)}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <LoadingAnimation size={18} />
          ) : (
            <Text style={[styles.cancelBtnText, { color: colors.textSecondary }]}>Cancel</Text>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  const renderEmptyState = (type) => {
    const emptyStates = {
      friends: {
        icon: 'people-outline',
        title: 'No friends yet',
        subtitle: 'Find players on the map and add them as friends!',
      },
      requests: {
        icon: 'mail-outline',
        title: 'No pending requests',
        subtitle: 'When someone sends you a friend request, it will appear here.',
      },
      sent: {
        icon: 'send',
        title: 'No sent requests',
        subtitle: 'Friend requests you send will appear here.',
      },
    };

    const state = emptyStates[type];

    return (
      <View style={styles.emptyState}>
        <View style={[styles.emptyIconCircle, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5' }]}>
          <Icon name={state.icon} size={48} color={colors.primary} />
        </View>
        <Text style={[styles.emptyTitle, { color: colors.text }]}>{state.title}</Text>
        <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>{state.subtitle}</Text>
      </View>
    );
  };

  const tabs = [
    { key: 'friends', label: 'Friends', count: friends.length },
    { key: 'requests', label: 'Requests', count: pendingRequests.length, highlight: pendingRequests.length > 0 },
    { key: 'sent', label: 'Sent', count: sentRequests.length },
  ];

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={200} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={[styles.headerContent, isDesktop && { maxWidth: MAX_CONTENT_WIDTH, alignSelf: 'center', width: '100%' }]}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Icon name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.text }]}>Friends</Text>
          <View style={styles.headerSpacer} />
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabsContainer, { borderBottomColor: colors.border }]}>
        <View style={[styles.tabs, isDesktop && { maxWidth: MAX_CONTENT_WIDTH, alignSelf: 'center', width: '100%' }]}>
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tab,
                activeTab === tab.key && styles.activeTab,
                activeTab === tab.key && { borderBottomColor: colors.primary },
              ]}
              onPress={() => setActiveTab(tab.key)}
            >
              <Text
                style={[
                  styles.tabText,
                  { color: activeTab === tab.key ? colors.primary : colors.textSecondary },
                ]}
              >
                {tab.label}
              </Text>
              {tab.count > 0 && (
                <View
                  style={[
                    styles.tabBadge,
                    { backgroundColor: tab.highlight ? colors.primary : isDarkMode ? colors.backgroundDark : '#e0e0e0' },
                  ]}
                >
                  <Text
                    style={[
                      styles.tabBadgeText,
                      { color: tab.highlight ? '#fff' : colors.text },
                    ]}
                  >
                    {tab.count}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Content */}
      <View style={[styles.content, isDesktop && { maxWidth: MAX_CONTENT_WIDTH, alignSelf: 'center', width: '100%' }]}>
        {activeTab === 'friends' && (
          <FlatList
            data={friends}
            renderItem={renderFriendItem}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={() => renderEmptyState('friends')}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />
            }
          />
        )}

        {activeTab === 'requests' && (
          <FlatList
            data={pendingRequests}
            renderItem={renderRequestItem}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={() => renderEmptyState('requests')}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />
            }
          />
        )}

        {activeTab === 'sent' && (
          <FlatList
            data={sentRequests}
            renderItem={renderSentRequestItem}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={() => renderEmptyState('sent')}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />
            }
          />
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    borderBottomWidth: 1,
    paddingVertical: 12,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
  },
  headerSpacer: {
    width: 40,
  },
  tabsContainer: {
    borderBottomWidth: 1,
  },
  tabs: {
    flexDirection: 'row',
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomWidth: 2,
  },
  tabText: {
    fontSize: 15,
    fontWeight: '600',
  },
  tabBadge: {
    marginLeft: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    minWidth: 22,
    alignItems: 'center',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  listContent: {
    padding: 16,
    flexGrow: 1,
  },
  userCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
  },
  requestCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  avatarPlaceholder: {
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userDetails: {
    marginLeft: 12,
    flex: 1,
  },
  username: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  statusText: {
    fontSize: 13,
  },
  ratingText: {
    fontSize: 13,
    marginLeft: 4,
  },
  timeText: {
    fontSize: 13,
  },
  userActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  messageBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  requestActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  acceptBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 4,
  },
  acceptBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  declineBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyIconCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    textAlign: 'center',
    paddingHorizontal: 40,
  },
});

export default FriendsScreen;

